import useDepositStats from 'hooks/useDepositStats';
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { Trash, ChevronDown, ChevronUp } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DropdownCard, BlockCard, Tag } from '@trellixio/roaster-coffee';
import { toast } from 'sonner';
import Page from 'components/shared/Page';
import Pagination from 'components/shared/Pagination';
import StatsGrid from 'components/shared/StatsGrid';
import FilterSelect from 'components/shared/FilterSelect';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import EmptyCard from 'components/shared/EmptyCard';
import { LockableButton } from 'components/shared/Lockable';
import { useAPI, useGetUser, useDebounce } from 'hooks';
import { formatDate, formatNumber, createDownloadable, preventSubmitWithENTER } from 'helpers';
import Locales from 'locales';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';

// Constants for filter labels and values used in trip filtering
const CONST_NONE = 'none';
const CONST_FILTER_LABEL_AGENCY = 'agency';
const CONST_FILTER_LABEL_INVOICE = 'invoice';
const CONST_FILTER_LABEL_DEPOSIT = 'deposit';
const CONST_FILTER_VALUE_DEPOSIT_CONFIRMED = 'deposit_confirmed';
const CONST_FILTER_VALUE_DEPOSIT_PENDING = 'deposit_pending';
const CONST_FILTER_LABEL_DATE = 'date';
const CONST_FILTER_VALUE_DATE_ASCENDING = 'asc';
const CONST_FILTER_VALUE_DATE_DESCENDING = 'desc';
const normalizeFilters = (filters) => (filters && Object.keys(filters).length > 0 ? filters : null);
const getDepositAppliedFilters = ({ agency, invoice, deposit, date }) =>
  normalizeFilters({
    ...(agency && agency !== CONST_NONE ? { [CONST_FILTER_LABEL_AGENCY]: agency } : {}),
    ...(invoice && invoice !== CONST_NONE ? { [CONST_FILTER_LABEL_INVOICE]: invoice } : {}),
    ...(deposit && deposit !== CONST_NONE ? { [CONST_FILTER_LABEL_DEPOSIT]: deposit } : {}),
    ...(date && date !== CONST_NONE ? { [CONST_FILTER_LABEL_DATE]: date } : {}),
  });

const DepositItem = ({ deposit, isAgency, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border-b last:border-b-0 border-gray-200 bg-white transition-colors hover:bg-muted/30">
      <div
        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-3 cursor-pointer select-none"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-gray-500 hover:text-gray-900"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Masquer les détails' : 'Afficher les détails'}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 truncate">
                {deposit.pilgrim_name || 'Pèlerin non précisé'}
              </span>
              <span className="text-xs text-gray-500">• {formatDate(deposit.created)}</span>
            </div>
            <div className="text-xs text-gray-500 truncate mt-0.5">
              {deposit.agency_name} {deposit.invoice_code ? `| Facture ${deposit.invoice_code}` : ''}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100">
          <span className="font-bold text-gray-900 tabular-nums text-sm sm:text-base">
            {`${formatNumber(deposit.amount)} F CFA`}
          </span>

          {deposit.is_invoice_paid ? (
            <Badge variant="success">Payé</Badge>
          ) : (
            <Badge variant="pending">En attente</Badge>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-2 bg-gray-50/80 border-t border-gray-100 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <span className="text-gray-500 block font-medium">Facture</span>
              <span className="font-semibold text-gray-900">{deposit.invoice_code || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500 block font-medium">Passeport</span>
              <span className="font-semibold text-gray-900">{deposit.pilgrim_passport || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500 block font-medium">Date de dépôt</span>
              <span className="font-semibold text-gray-900">{formatDate(deposit.created)}</span>
            </div>
            <div>
              <span className="text-gray-500 block font-medium">Société / Agence</span>
              <span className="font-semibold text-gray-900">{deposit.agency_name || '-'}</span>
            </div>
          </div>

          {isAgency && !deposit.is_invoice_paid && (
            <div className="flex justify-end pt-2 border-t border-gray-200/60">
              <LockableButton
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 gap-1.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(deposit);
                }}
              >
                <Trash className="h-3.5 w-3.5" />
                <span>Supprimer le dépôt</span>
              </LockableButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

DepositItem.propTypes = {
  deposit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    created: PropTypes.string,
    agency_name: PropTypes.string,
    pilgrim_name: PropTypes.string,
    pilgrim_passport: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoice_code: PropTypes.string,
    is_invoice_paid: PropTypes.bool,
  }).isRequired,
  isAgency: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
};

const DepositsPage = () => {
  const { isAGLO, isAgency } = useGetUser();
  const { api } = useAPI();

  const [cursor, setCursor] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const preSetAgency = isAGLO ? searchParams.get(CONST_FILTER_LABEL_AGENCY) || CONST_NONE : CONST_NONE;
  const preSetInvoice = searchParams.get(CONST_FILTER_LABEL_INVOICE) || CONST_NONE;
  const preSetPaymentStatus = searchParams.get(CONST_FILTER_LABEL_DEPOSIT) || CONST_NONE;
  const preSetDate = searchParams.get(CONST_FILTER_LABEL_DATE) || CONST_NONE;
  const preSetSearch = searchParams.get('q') || '';

  const [search, setSearch] = useState(preSetSearch);
  const [selectedAgency, setAgency] = useState(preSetAgency);
  const [selectedInvoice, setInvoice] = useState(preSetInvoice);
  const [selectedPaymentStatus, setPaymentStatus] = useState(preSetPaymentStatus);
  const [selectedDate, setDate] = useState(preSetDate);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const debouncedSearchQuery = useDebounce(search, 1000);
  const queryClient = useQueryClient();
  const [queryFilters, setQueryFilter] = useState(() =>
    getDepositAppliedFilters({
      agency: preSetAgency,
      invoice: preSetInvoice,
      deposit: preSetPaymentStatus,
      date: preSetDate,
    })
  );
  const appliedAgency = queryFilters?.[CONST_FILTER_LABEL_AGENCY] || CONST_NONE;
  const appliedInvoice = queryFilters?.[CONST_FILTER_LABEL_INVOICE] || CONST_NONE;
  const appliedPaymentStatus = queryFilters?.[CONST_FILTER_LABEL_DEPOSIT] || CONST_NONE;
  const appliedDate = queryFilters?.[CONST_FILTER_LABEL_DATE] || CONST_NONE;
  const effectiveSearchQuery = search ? debouncedSearchQuery : '';

  const fetchParams = useMemo(
    () => ({
      ...(queryFilters || {}),
      ...(effectiveSearchQuery ? { q: effectiveSearchQuery } : {}),
      ...(cursor ? { cursor } : {}),
    }),
    [cursor, effectiveSearchQuery, queryFilters]
  );
  const paginationKey = useMemo(
    () => JSON.stringify({ ...(queryFilters || {}), q: search || '' }),
    [queryFilters, search]
  );

  const { data: deposits, isFetching } = useQuery({
    queryFn: api.list('deposit/', fetchParams),
    queryKey: ['deposit/', fetchParams],
    keepPreviousData: true,
  });

  const { data: agencies } = useQuery({
    queryFn: api.list('agency/'),
    queryKey: ['agency/'],
    enabled: isAGLO,
  });

  const { data: invoices } = useQuery({
    queryFn: api.list('invoice/'),
    queryKey: ['invoice/'],
  });

  const { data: depositsStatsData } = useDepositStats();

  const depositsStats = depositsStatsData?.data.data;

  const isInvoiceOnlyDeposit = (invoiceCode) =>
    deposits.filter((deposit) => deposit.invoice_code === invoiceCode).length === 1;

  const exportListParams = useMemo(
    () =>
      new URLSearchParams({
        ...(queryFilters || {}),
        ...(effectiveSearchQuery ? { q: effectiveSearchQuery } : {}),
      }).toString(),
    [effectiveSearchQuery, queryFilters]
  );

  const { mutate: deleteDeposit } = useMutation({
    mutationFn: (deposit) => api.remove('deposit', deposit.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['invoice/'] });
      queryClient.invalidateQueries({ queryKey: ['deposit/'] });
      queryClient.invalidateQueries({ queryKey: ['deposit/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] });
      toast.success(Locales.deposit.toast.delete_deposit);
    },
  });

  const { mutate: exportDepositsList } = useMutation({
    mutationFn: () => api.get(`deposit/export/csv/?${exportListParams}`),
    onSuccess: ({ data }) => {
      createDownloadable(data, 'Liste des dépôts', 'csv');
      toast.success('Téléchargement réussi');
    },
  });

  const handleDelete = (deposit) => {
    setOpenDeleteModal(true);
    setSelectedItem(deposit);
  };

  const resultString =
    deposits?.count > 1
      ? `${deposits.count} ${Locales.deposit.title.toLowerCase()}`
      : `${deposits?.count || 0} ${Locales.deposit.title.toLowerCase().slice(0, -1)}`;

  const selectableListDefaultOption = [{ label: Locales.base.button.select, value: CONST_NONE, disabled: true }];

  const paymentStatus = [
    { label: Locales.deposit.filter.payment_status.deposit_confirmed, value: CONST_FILTER_VALUE_DEPOSIT_CONFIRMED },
    { label: Locales.deposit.filter.payment_status.deposit_pending, value: CONST_FILTER_VALUE_DEPOSIT_PENDING },
  ];

  const creationDate = [
    { label: Locales.deposit.filter.creation_date.date_ascending, value: CONST_FILTER_VALUE_DATE_ASCENDING },
    { label: Locales.deposit.filter.creation_date.date_descending, value: CONST_FILTER_VALUE_DATE_DESCENDING },
  ];

  const selectedAgencyName = () => {
    let name = null;
    if (appliedAgency !== CONST_NONE) {
      agencies?.forEach((data) => {
        if (data.id === appliedAgency) name = data.name;
      });
    }

    return name;
  };

  const selectedInvoiceCode = () => {
    let code = null;
    if (appliedInvoice !== CONST_NONE) {
      invoices?.forEach((data) => {
        if (data.id === appliedInvoice) code = data.code;
      });
    }

    return code;
  };

  const selectedPaymentStatusName = () => {
    let label = null;
    if (appliedPaymentStatus !== CONST_NONE) {
      paymentStatus.forEach((data) => {
        if (data.value === appliedPaymentStatus) label = data.label;
      });
    }

    return label;
  };

  const selectedCreationDate = () => {
    let label = null;
    if (appliedDate !== CONST_NONE) {
      creationDate.forEach((data) => {
        if (data.value === appliedDate) label = data.label;
      });
    }

    return label;
  };

  const filterOptions = [
    {
      id: CONST_FILTER_LABEL_AGENCY,
      label: Locales.trip.filter.agreed_agency,
      helpText: Locales.deposit.filter.helptext.agreed_agency,
      value: selectedAgency,
      data: selectableListDefaultOption.concat(agencies || []),
      onChange: setAgency,
      tagName: selectedAgencyName(),
      condition: isAGLO,
    },
    {
      id: CONST_FILTER_LABEL_DEPOSIT,
      label: Locales.base.dropdown.payment_status,
      helpText: Locales.deposit.filter.helptext.payment_status,
      value: selectedPaymentStatus,
      data: selectableListDefaultOption.concat(paymentStatus),
      onChange: setPaymentStatus,
      tagName: selectedPaymentStatusName(),
      condition: isAGLO || isAgency,
    },
    {
      id: CONST_FILTER_LABEL_INVOICE,
      label: Locales.base.dropdown.invoice,
      helpText: Locales.deposit.filter.helptext.invoice,
      value: selectedInvoice,
      data: selectableListDefaultOption.concat(
        (invoices || []).map((invoice) => ({
          label: invoice.code,
          value: invoice.id,
        }))
      ),
      onChange: setInvoice,
      tagName: selectedInvoiceCode(),
      condition: isAGLO || isAgency,
    },
    {
      id: CONST_FILTER_LABEL_DATE,
      label: Locales.base.dropdown.sort_by_date,
      helpText: Locales.deposit.filter.helptext.date,
      value: selectedDate,
      data: selectableListDefaultOption.concat(creationDate),
      onChange: setDate,
      tagName: selectedCreationDate(),
      condition: isAGLO || isAgency,
    },
  ];

  // Function to apply selected filters
  const applyFilters = () => {
    setQueryFilter(
      getDepositAppliedFilters({
        agency: selectedAgency,
        invoice: selectedInvoice,
        deposit: selectedPaymentStatus,
        date: selectedDate,
      })
    );
  };

  // Function to reset all filters to their default state (CONST_NONE)
  const resetFilters = () => {
    setAgency(CONST_NONE);
    setInvoice(CONST_NONE);
    setPaymentStatus(CONST_NONE);
    setDate(CONST_NONE);
    setQueryFilter(null);
  };

  // Function to remove a specific filter from the query filters
  const deleteFilter = (filter) => {
    const queryFiltersLocal = { ...queryFilters };
    delete queryFiltersLocal[filter];
    setQueryFilter(normalizeFilters(queryFiltersLocal));
  };

  // Function to remove a specific filter and reset its corresponding state
  const removeFilter = (filter) => {
    switch (filter) {
      case CONST_FILTER_LABEL_AGENCY:
        setAgency(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_INVOICE:
        setInvoice(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_DEPOSIT:
        setPaymentStatus(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_DATE:
        setDate(CONST_NONE);
        break;
      default:
        break;
    }
    deleteFilter(filter);
  };

  useEffect(() => {
    setSearch(preSetSearch);
    setAgency(preSetAgency);
    setInvoice(preSetInvoice);
    setPaymentStatus(preSetPaymentStatus);
    setDate(preSetDate);
    setQueryFilter(
      getDepositAppliedFilters({
        agency: preSetAgency,
        invoice: preSetInvoice,
        deposit: preSetPaymentStatus,
        date: preSetDate,
      })
    );
  }, [preSetAgency, preSetDate, preSetInvoice, preSetPaymentStatus, preSetSearch]);

  useEffect(() => {
    let params = {};

    if (queryFilters) {
      params = { ...queryFilters };
    }

    if (search) {
      params = { ...params, q: search };
    }

    if (Object.keys(params).length > 0) {
      setSearchParams(params);
      return;
    }

    setSearchParams({});
  }, [queryFilters, search, setSearchParams]);

  useEffect(() => {
    setCursor('');
  }, [paginationKey]);

  return (
    <Page
      title="Dépôts"
      description="Liste des dépôts effectués par les pèlerins"
      onSearch={(value) => setSearch(value.trim())}
      stats={depositsStats && <StatsGrid stats={depositsStats} />}
      onPrint={deposits && deposits.length > 0 ? exportDepositsList : undefined}
      printTooltip="Télécharger la liste des dépôts"
    >
      {!deposits && <EmptyCard />}

      {deposits && (
        <>
          <div className="flex flex-row gap-2">
            <Input
              placeholder="Rechercher une facture"
              className="w-full rounded-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={preventSubmitWithENTER}
            />

            <DropdownCard>
              <DropdownCard.Trigger>
                <Button variant="outline" type="button">
                  <i className="fa-solid fa-regular fa-bars-filter" /> {Locales.base.dropdown.filter}
                </Button>
              </DropdownCard.Trigger>
              <DropdownCard.Dropdown width="large">
                <DropdownCard.Content>
                  <form>
                    {filterOptions.map((option) =>
                      option.condition ? (
                        <FilterSelect
                          key={option.id}
                          label={option.label}
                          helpText={option.helpText}
                          value={option.value}
                          data={option.data}
                          onChange={option.onChange}
                        />
                      ) : null
                    )}
                  </form>
                </DropdownCard.Content>
                <DropdownCard.Actions>
                  <Button variant="link" type="button" onClick={resetFilters}>
                    {Locales.base.button.reset}
                  </Button>

                  <Button variant="hajj" type="button" onClick={applyFilters}>
                    {Locales.base.button.validate}
                  </Button>
                </DropdownCard.Actions>
              </DropdownCard.Dropdown>
            </DropdownCard>
          </div>

          <div className="flex gap-4 mb-4 items-center justify-between flex-col md:flex-row">
            <BlockCard className="w-full m-0">
              <div className="table-meta items-group">
                <div>
                  <span>{Locales.base.result} : </span>
                  <strong>{resultString}</strong>
                </div>

                <div className="tags-container">
                  {queryFilters &&
                    filterOptions.map(
                      (filter) =>
                        queryFilters[filter.id] && (
                          <>
                            <span>|</span>
                            <Tag onRemove={() => removeFilter(filter.id)}>{`${filter.label}: ${filter.tagName}`}</Tag>
                          </>
                        )
                    )}
                </div>
              </div>
            </BlockCard>
          </div>
          <div className="rounded-sm overflow-hidden border border-gray-200 bg-white">
            {deposits.length === 0 ? (
              <div className="p-8 text-center text-gray-700 font-bold text-sm">Aucune donnée disponible</div>
            ) : (
              <div className="divide-y divide-gray-200">
                {deposits.map((deposit) => (
                  <DepositItem key={deposit.id} deposit={deposit} isAgency={isAgency} onDelete={handleDelete} />
                ))}
              </div>
            )}

            <Pagination key={paginationKey} data={deposits} isFetching={isFetching} setCursor={setCursor} />
          </div>
        </>
      )}

      {selectedItem && (
        <ConfirmationModal
          isOpen={openDeleteModal}
          setOpenModal={setOpenDeleteModal}
          title={Locales.deposit.suppress_deposit}
          confirmBtnVariant="destructive"
          confirmBtnLabel={Locales.base.button.delete}
          message={
            <p>
              {Locales.formatString(Locales.deposit.delete_deposit, {
                amount: <strong>{formatNumber(selectedItem.amount)}</strong>,
                fullName: <strong>{selectedItem.pilgrim_name}</strong>,
              })}

              {isInvoiceOnlyDeposit(selectedItem.invoice_code) &&
                Locales.formatString(Locales.deposit.warning_last_deposit, {
                  code: <strong>{selectedItem.invoice_code}</strong>,
                })}
            </p>
          }
          onConfirm={() => deleteDeposit(selectedItem)}
        />
      )}
    </Page>
  );
};

export default DepositsPage;
